<template>
  <base-section
    id="product-intro"
    space="50"
  >
    <!-- 面包屑 -->
    <v-container
      class="py-0"
      style="max-width: 1160px"
    >
      <base-breadcrumb :items="breadcrumbItems" />
    </v-container>

    <base-section-heading
      class="seo-only-heading"
      color="#142d7a"
      :title="productHeadingTitle"
      :subtitle="productHeadingSubtitle"
      :mobile-size="'text-h5'"
      :size="'text-h4'"
      :space="isMobile ? 2 : 12"
      title-tag="h1"
      :is-uppercase="false"
    />

    <!-- 导航栏 -->
    <div class="product-tab-nav">
      <div class="product-tab-nav__inner">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="product-tab-nav__item"
          :class="{ 'product-tab-nav__item--active': activeTab === tab.key }"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Introduce 区块 -->
    <div
      v-show="activeTab === 'introduce'"
      id="section-introduce"
      class="section-anchor"
    >
      <v-container class="pa-0 custom-container">
        <v-row
          v-if="loading"
          class="ma-0"
        >
          <v-col
            cols="12"
            class="text-center py-12"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            />
            <p class="mt-4">
              {{ $t('common.loading') }}
            </p>
          </v-col>
        </v-row>
        <v-row
          v-else
          class="ma-0"
        >
          <v-col
            cols="12"
            class="pa-0"
          >
            <div
              v-if="productSummary"
              class="product-summary"
              v-html="productSummary"
            />
            <div
              v-else
              class="text-center py-12"
            >
              {{ $t('common.noData') }}
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Specs 区块 -->
    <div
      v-show="activeTab === 'specs'"
      id="section-specs"
      class="section-anchor"
    >
      <v-container
        v-if="specs.length > 0"
        class="pa-0 custom-container"
        style="padding-top: 48px !important; padding-bottom: 24px !important;"
      >
        <div class="section-content-wrap">
          <div
            v-for="group in specGroups"
            :key="group.sectionKey"
            class="spec-group"
          >
            <div
              v-if="group.sectionLabel"
              class="spec-group__header"
            >
              {{ group.sectionLabel }}
            </div>
            <table class="spec-table">
              <tbody>
                <tr
                  v-for="item in group.items"
                  :key="item.id"
                  class="spec-row"
                >
                  <td class="spec-label">
                    {{ item.specLabel }}
                  </td>
                  <td class="spec-value">
                    {{ item.specValue }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-container>
    </div>

    <!-- FAQs 区块 -->
    <div
      v-show="activeTab === 'faqs'"
      id="section-faqs"
      class="section-anchor"
    >
      <v-container
        v-if="faqs.length > 0"
        class="pa-0 custom-container"
        style="padding-top: 48px !important; padding-bottom: 48px !important;"
      >
        <div class="faq-list">
          <div
            v-for="faq in faqs"
            :key="faq.id"
            class="faq-item"
          >
            <button
              class="faq-question"
              :aria-expanded="faq._open"
              @click="toggleFaq(faq)"
            >
              <span>{{ faq.question }}</span>
              <v-icon
                class="faq-icon"
                :class="{ 'faq-icon--open': faq._open }"
              >
                mdi-chevron-down
              </v-icon>
            </button>
            <div
              v-if="faq._open"
              class="faq-answer"
              v-html="sanitizeHtml(faq.answer || '')"
            />
          </div>
        </div>
      </v-container>
    </div>

    <!-- Videos 区块 -->
    <div
      v-show="activeTab === 'videos'"
      id="section-videos"
      class="section-anchor"
    >
      <v-container
        v-if="videos.length > 0"
        class="pa-0 custom-container"
        style="padding-top: 48px !important; padding-bottom: 48px !important;"
      >
        <div class="video-list">
          <div
            v-for="video in videos"
            :key="video.id"
            class="video-item"
          >
            <div
              v-if="video.title"
              class="video-title"
            >
              {{ video.title }}
            </div>
            <div class="video-wrapper">
              <iframe
                v-if="isEmbeddable(video.videoUrl)"
                :src="toEmbedUrl(video.videoUrl)"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="video-iframe"
              />
              <video
                v-else
                :src="video.videoUrl"
                controls
                class="video-player"
              />
            </div>
          </div>
        </div>
      </v-container>
    </div>
  </base-section>
</template>

<script>
  import { SEO_LANGS } from '@/utils/seo'
  import { sanitizeHtml } from '@/utils/sanitize'

  export default {
    name: 'ProductIntro',

    components: {},

    data: () => ({
      product: null,
      productSummary: '',
      productTitle: '',
      productDescription: '',
      loading: false,
      activeTab: 'introduce',
      specs: [],
      specsLoading: false,
      faqs: [],
      faqsLoading: false,
      videos: [],
      videosLoading: false,
    }),

    metaInfo() {
      const title = this.productHeadingTitle
      const description = this.productHeadingSubtitle || this.$t('products.subtitle')
      const keywords = [title, 'Yooxplore', this.$t('products.title')].filter(Boolean).join(', ')
      const metaInfo = {
        title,
        meta: [
          { vmid: 'description', name: 'description', content: description },
          { vmid: 'keywords', name: 'keywords', content: keywords },
        ],
      }

      if (this.productJsonLd) {
        metaInfo.script = [
          {
            vmid: 'product-jsonld',
            type: 'application/ld+json',
            json: this.productJsonLd,
          },
        ]
      }

      return metaInfo
    },

    computed: {
      siteUrl() {
        return (process.env.VUE_APP_SITE_URL || 'https://yooxplore.com').replace(/\/$/, '')
      },
      productHeadingTitle() {
        return this.productTitle || this.product?.name || this.product?.title || this.$t('products.title')
      },
      productHeadingSubtitle() {
        return this.productDescription || this.product?.desc || this.$t('products.subtitle')
      },
      productJsonLd() {
        if (!this.product) return null
        const image = this.absoluteUrl(this.product.coverImage || this.product.imageUrl || '')
        const json = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: this.productHeadingTitle,
          description: this.productHeadingSubtitle,
          url: `${this.siteUrl}${this.canonicalPath}`,
          brand: {
            '@type': 'Brand',
            name: 'Yooxplore',
          },
        }
        if (image) json.image = image
        if (this.product.category) json.category = this.product.category
        return json
      },
      isMobile () {
        return this.$vuetify.breakpoint.width <= 768
      },
      tabs () {
        return [
          { key: 'introduce', label: this.$t('products.tab.introduce') },
          { key: 'specs', label: this.$t('products.tab.specs') },
          { key: 'faqs', label: this.$t('products.tab.faqs') },
          { key: 'videos', label: this.$t('products.tab.videos') },
        ]
      },
      breadcrumbItems () {
        const lang = this.$i18n.locale
        const items = [
          { text: this.$t('nav.home'), to: `/${lang}` },
          { text: this.$t('nav.products'), to: `/${lang}/products` },
        ]
        if (this.productTitle) {
          items.push({ text: this.productTitle })
        }
        return items
      },
      specGroups () {
        const map = new Map()
        for (const spec of this.specs) {
          // 优先用 sectionKey（去空格）分组；未设置则用 sectionLabel 作为分组键
          const key = (spec.sectionKey || '').trim() || (spec.sectionLabel || '').trim() || ''
          if (!map.has(key)) {
            map.set(key, {
              sectionKey: key,
              sectionLabel: spec.sectionLabel || '',
              minOrder: spec.sortOrder ?? 999,
              items: [],
            })
          }
          const group = map.get(key)
          // 取组内第一个非空的 sectionLabel
          if (!group.sectionLabel && spec.sectionLabel) {
            group.sectionLabel = spec.sectionLabel
          }
          group.items.push(spec)
          if ((spec.sortOrder ?? 999) < group.minOrder) {
            group.minOrder = spec.sortOrder ?? 999
          }
        }
        // 组内按 sortOrder 排序，组间按组内最小 sortOrder 排序
        const groups = Array.from(map.values())
        groups.forEach(g => g.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
        return groups.sort((a, b) => a.minOrder - b.minOrder)
      },
      canonicalPath() {
        return this.product?.slug ? `/${this.$i18n.locale}/products/${this.product.slug}` : this.$route.path
      },
    },

    watch: {
      '$route.params.itemId': {
        handler (newId) {
          this.loadAll(newId)
        },
        immediate: true,
      },
      '$i18n.locale' () {
        if (this.$route.params.itemId) {
          this.loadAll(this.$route.params.itemId)
        }
      },
    },

    mounted () {
    },

    beforeDestroy () {
    },

    methods: {
      // 将 sanitizeHtml 注册为组件方法，使模板中可直接调用
      sanitizeHtml,
      getSeoCanonicalPath() {
        return this.canonicalPath
      },
      getSeoAlternatePaths() {
        if (!this.product?.slug) return null
        return SEO_LANGS.reduce((acc, lang) => {
          acc[lang] = `/${lang}/products/${this.product.slug}`
          return acc
        }, {})
      },
      stripHtml(value) {
        if (!value) return ''
        return String(value)
          .replace(/<script[\s\S]*?<\/script>/gi, ' ')
          .replace(/<style[\s\S]*?<\/style>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      },
      truncate(value, maxLength = 160) {
        if (!value) return ''
        const text = String(value).trim()
        return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}...` : text
      },
      absoluteUrl(value) {
        if (!value) return ''
        if (/^https?:\/\//i.test(value)) return value
        return `${this.siteUrl}/${String(value).replace(/^\/+/, '')}`
      },
      async loadAll (itemId) {
        if (!itemId) {
          this.product = null
          this.productTitle = ''
          this.productDescription = ''
          this.productSummary = ''
          this.specs = []
          this.faqs = []
          this.videos = []
          return
        }
        // 优先用 slug 查询；如果 itemId 是纯数字则降级用 ID 查询
        const isNumericId = /^\d+$/.test(String(itemId))
        // 检测旧格式 {id}-{slug}（如 "13-yoox-s3t-drone"）
        const oldFormatMatch = String(itemId).match(/^(\d+)-(.+)$/)
        let product = null
        if (isNumericId) {
          product = await this.loadProductSummary(null, itemId, false)
        } else {
          // slug 查询；如果有旧格式降级方案则先不跳 404
          product = await this.loadProductSummary(itemId, null, !!oldFormatMatch)
          // 如果 slug 查询失败且匹配旧格式，用数字 ID 降级查询
          if (!product && oldFormatMatch) {
            product = await this.loadProductSummary(null, oldFormatMatch[1], false)
          }
        }
        const productId = product?.id
        if (productId) {
          await Promise.all([
            this.loadSpecs(productId),
            this.loadFaqs(productId),
            this.loadVideos(productId),
          ])
        }
      },

      async loadProductSummary (slug, id, suppressRedirect = false) {
        this.loading = true
        try {
          const url = slug ? `/public/products/by-slug/${slug}` : `/public/products/${id}`
          const product = await this.$axios({
            method: 'get',
            url,
            params: { lang: this.$i18n.locale },
          })
          if (!product) {
            console.warn('Product not found')
            if (!suppressRedirect) {
              this.product = null
              this.productTitle = ''
              this.productDescription = ''
              this.productSummary = ''
              this.$router.replace({ name: 'FourOhFour', params: { lang: this.$i18n.locale } })
            }
            return null
          }
          this.product = product
          this.productTitle = product.title || product.name || ''
          this.productDescription = this.truncate(this.stripHtml(product.desc || product.summary || ''))
          // 清洗富文本并为空 ALT 图片补充产品名（SEO）
          let html = sanitizeHtml(product.summary || '')
          if (this.productTitle) {
            html = html.replace(/<img([^>]*)\balt=['"](\s*)['"]>/gi, `<img$1alt='${this.productTitle}'>`)
          }
          this.productSummary = html

          if (product.slug && this.$route.params.itemId !== product.slug) {
            this.$router.replace({
              name: 'ProductsItem',
              params: { lang: this.$i18n.locale, itemId: product.slug },
              query: this.$route.query,
            })
          }
          return product
        } catch (error) {
          console.error('加载产品详情失败:', error)
          if (!suppressRedirect) {
            this.product = null
            this.productTitle = ''
            this.productDescription = ''
            this.productSummary = ''
            this.$router.replace({ name: 'FourOhFour', params: { lang: this.$i18n.locale } })
          }
          return null
        } finally {
          this.loading = false
        }
      },

      async loadSpecs (id) {
        this.specsLoading = true
        try {
          // 先加载当前语言，若该语言无数据则 fallback 英文
          let data = await this.$axios({
            method: 'get',
            url: `/public/products/${id}/specs`,
            params: { lang: this.$i18n.locale },
          })
          data = (data || []).filter(s => s.visible !== false)
          // 如果非英文且拿到的数据项 specLabel 全为空，则回落英文
          if (this.$i18n.locale !== 'en' && data.every(s => !s.specLabel)) {
            const enData = await this.$axios({
              method: 'get',
              url: `/public/products/${id}/specs`,
              params: { lang: 'en' },
            })
            data = (enData || []).filter(s => s.visible !== false)
          }
          this.specs = data
        } catch (error) {
          console.error('加载产品参数失败:', error)
          this.specs = []
        } finally {
          this.specsLoading = false
        }
      },

      async loadFaqs (id) {
        this.faqsLoading = true
        try {
          let data = await this.$axios({
            method: 'get',
            url: `/public/products/${id}/faqs`,
            params: { lang: this.$i18n.locale },
          })
          data = (data || []).filter(f => f.visible !== false)
          // fallback 英文
          if (this.$i18n.locale !== 'en' && data.every(f => !f.question)) {
            const enData = await this.$axios({
              method: 'get',
              url: `/public/products/${id}/faqs`,
              params: { lang: 'en' },
            })
            data = (enData || []).filter(f => f.visible !== false)
          }
          this.faqs = data.map(f => ({ ...f, _open: false }))
        } catch (error) {
          console.error('加载产品 FAQ 失败:', error)
          this.faqs = []
        } finally {
          this.faqsLoading = false
        }
      },

      switchTab (key) {
        this.activeTab = key
      },

      scrollToSection (key) {
        const el = document.getElementById(`section-${key}`)
        if (!el) return
        const nav = document.querySelector('.product-tab-nav')
        const navH = nav ? nav.offsetHeight : 60
        const top = el.getBoundingClientRect().top + window.scrollY - navH - 16
        window.scrollTo({ top, behavior: 'smooth' })
        this.activeTab = key
      },

      handleScroll () {
        const nav = document.querySelector('.product-tab-nav')
        if (!nav) return
        const rect = nav.getBoundingClientRect()
        this.navSticky = rect.top <= 0

        const sections = ['introduce', 'specs', 'faqs']
        const scrollY = window.scrollY
        const navH = nav.offsetHeight
        let current = 'introduce'
        for (const key of sections) {
          const el = document.getElementById(`section-${key}`)
          if (el && el.offsetTop - navH - 32 <= scrollY) {
            current = key
          }
        }
        this.activeTab = current
      },

      toggleFaq (faq) {
        faq._open = !faq._open
      },

      async loadVideos (id) {
        this.videosLoading = true
        try {
          const data = await this.$axios({
            method: 'get',
            url: `/public/products/${id}/videos`,
          })
          this.videos = (data || []).filter(v => v.visible !== false)
        } catch (error) {
          console.error('加载产品视频失败:', error)
          this.videos = []
        } finally {
          this.videosLoading = false
        }
      },

      /** 判断是否为可嵌入的视频链接（YouTube / Vimeo） */
      isEmbeddable (url) {
        if (!url) return false
        return /youtube\.com|youtu\.be|vimeo\.com/.test(url)
      },

      /** 将 YouTube / Vimeo 链接转为嵌入式 URL */
      toEmbedUrl (url) {
        if (!url) return ''
        // YouTube: https://www.youtube.com/watch?v=xxx 或 https://youtu.be/xxx
        const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
        if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
        // Vimeo: https://vimeo.com/xxx
        const vmMatch = url.match(/vimeo\.com\/(\d+)/)
        if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`
        return url
      },
    },
  }
</script>

<style lang="sass" scoped>
.custom-container
  max-width: none !important

/* 导航栏（非粘性） */
.product-tab-nav
  position: relative
  z-index: 4
  padding: 0
  margin-bottom: 8px

.product-tab-nav__inner
  display: flex
  justify-content: flex-start
  gap: 48px
  max-width: 1160px
  margin: 0 auto
  padding: 0 12px
  overflow-x: auto
  box-sizing: border-box

.product-tab-nav__item
  position: relative
  padding: 8px 0
  font-size: 14px
  font-weight: 500
  color: rgba(0, 0, 0, 0.72)
  border: none
  background: transparent
  cursor: pointer
  transition: all 0.2s
  white-space: nowrap
  outline: none

  &:hover
    color: rgba(0, 0, 0, 0.92)

.product-tab-nav__item--active
  color: rgba(0, 0, 0, 0.92) !important
  font-weight: 700

  &::after
    content: ''
    position: absolute
    left: 0
    right: 0
    bottom: 0
    height: 2px
    background: #e65f24

/* 区块标题 */
.section-content-wrap
  max-width: 1160px
  margin: 0 auto
  padding: 0 16px

.section-title
  font-size: 22px
  font-weight: 700
  color: #142d7a
  margin-bottom: 24px
  text-align: center

/* 保留 H1 语义给搜索引擎和辅助技术，但不占用视觉版面 */
.seo-only-heading
  position: absolute !important
  width: 1px !important
  height: 1px !important
  padding: 0 !important
  margin: -1px !important
  overflow: hidden !important
  clip: rect(0, 0, 0, 0) !important
  clip-path: inset(50%) !important
  white-space: nowrap !important
  border: 0 !important

/* Specs 表格 */
.spec-group
  margin-bottom: 32px

.spec-group__header
  font-size: 15px
  font-weight: 700
  color: rgba(0, 0, 0, 0.78)
  padding: 10px 16px
  background: rgba(20, 45, 122, 0.05)
  border-left: 3px solid #142d7a
  margin-bottom: 0

.spec-table
  width: 100%
  border-collapse: collapse

.spec-row
  border-bottom: 1px solid rgba(0, 0, 0, 0.06)

  &:last-child
    border-bottom: none

.spec-label
  width: 40%
  padding: 12px 16px
  font-size: 14px
  font-weight: 700
  color: rgba(0, 0, 0, 0.75)
  vertical-align: top

.spec-value
  width: 60%
  padding: 12px 16px
  font-size: 14px
  color: rgba(0, 0, 0, 0.85)
  vertical-align: top
  white-space: pre-wrap
  word-break: break-word

/* FAQ */
.faq-list
  max-width: 1160px
  margin: 0 auto
  padding: 0 16px

.faq-item
  border-bottom: 1px solid rgba(0, 0, 0, 0.08)

  &:first-child
    border-top: 1px solid rgba(0, 0, 0, 0.08)

.faq-question
  width: 100%
  display: flex
  justify-content: space-between
  align-items: center
  padding: 18px 4px
  font-size: 15px
  font-weight: 500
  color: rgba(0, 0, 0, 0.85)
  background: transparent
  border: none
  cursor: pointer
  text-align: left
  outline: none
  gap: 12px

  &:hover
    color: #142d7a

.faq-icon
  flex-shrink: 0
  transition: transform 0.25s

.faq-icon--open
  transform: rotate(180deg)

.faq-answer
  padding: 0 4px 18px
  font-size: 14px
  line-height: 1.8
  color: rgba(0, 0, 0, 0.65)

.faq-answer :deep(p)
  margin: 0 0 8px

.faq-answer :deep(ul),
.faq-answer :deep(ol)
  padding-left: 20px
  margin: 0 0 8px

.faq-answer :deep(img)
  max-width: 100%
  height: auto
  border-radius: 4px

/* Introduce 内容 */
.product-summary
  max-width: 1160px
  margin: 0 auto
  font-size: 16px
  line-height: 1.8
  color: rgba(0, 0, 0, 0.78)

.product-summary :deep(p)
  margin: 0

.product-summary :deep(img)
  display: block
  width: 100%
  max-width: 100%
  height: auto

/* Videos */
.video-list
  max-width: 1160px
  margin: 0 auto
  padding: 0 16px
  display: flex
  flex-direction: column
  gap: 32px

.video-item
  width: 100%

.video-title
  font-size: 16px
  font-weight: 600
  color: rgba(0, 0, 0, 0.85)
  margin-bottom: 12px
  text-align: center

.video-wrapper
  position: relative
  width: 100%
  padding-bottom: 56.25%
  background: #000
  border-radius: 8px
  overflow: hidden

.video-iframe,
.video-player
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  border: none
  border-radius: 8px

.product-summary :deep(video)
  max-width: 100%

.section-anchor
  scroll-margin-top: 80px
</style>
